/** Trade-log view-model. Mirrors the shape of `GET /api/trades` and
 * `GET /api/alerts` responses. Nullable fields are ones the backend
 * doesn't yet persist (exit_premium, brokerage, rawPayload) or that
 * can't be derived from what's stored (bias / lots when contract
 * metadata is missing). */

export type ExitReason =
  | "TARGET_HIT"
  | "SL_HIT"
  /** A trailing reference ended it, in profit — the 5 EMA close on the EMA
   * trend / BB / bias engines. Separate from SL_HIT because it normally
   * books ABOVE entry; folding the two together reported every winning
   * trail as a loss. */
  | "TRAIL_EXIT"
  | "FORCE_SQUAREOFF"
  | "MANUAL"
  | "EXIT_FAILED"
  /** POI-1's per-lot rupee cap on the option (docs/poi1.md §6) — not the
   * index stop, which is SL_HIT. */
  | "LOSS_CAP"
  | "OPEN";

/** Deploy target: real broker orders vs synthetic paper fills (2026-07-10). */
export type ExecutionMode = "LIVE" | "PAPER";

/** Trade lifetime (docs/positional.md): INTRADAY (single contract, flat
 * by close) or POSITIONAL (multi-leg basket held overnight). */
export type Horizon = "INTRADAY" | "POSITIONAL";

export type LegStatus = "PENDING" | "FILLED" | "EXIT_PENDING" | "CLOSED" | "FAILED";

/** One leg of a positional basket row. */
export interface TradeLegEntry {
  legIndex: number;
  side: "BUY" | "SELL";
  contractSymbol: string;
  quantity: number;
  expiry: string; // YYYY-MM-DD
  status: LegStatus;
  entryFillPrice: number | null;
  exitFillPrice: number | null;
}

export interface TradeLogEntry {
  id: string;
  strategyId: string;
  strategyName: string;
  /** WHICH RULE inside the strategy opened the trade — bias's area
   * ("AREA_A" / "B1" / "B2" / "GAP_FILL"), the 5/50 EMA rule's case. The name
   * alone cannot say: one strategy runs several independent rules. Null on
   * rows written before 2026-08-24 and on single-rule strategies. */
  /** The contract's expiry. What makes a MONTHLY tradingsymbol linkable to a
   * chart — that form (NIFTY26AUG24250PE) carries no expiry day, so the ticker
   * cannot be built from the symbol alone. Null on rows written before
   * 2026-08-24; those keep falling back to plain text. */
  contractExpiry: string | null;
  signalSource: string | null;
  /** The rule's stop and target as INDEX levels, stamped at entry.
   *
   * ⚠️ NOT `currentSlPrice` / `targetPrice`, which are PREMIUM and only exist
   * when a broker stop backs the trade. Bias, BB and the EMA rule watch an
   * index level and park no order, so those are null and these carry the real
   * numbers. Never render the two in one column. */
  signalStop: number | null;
  signalTarget: number | null;
  /** The strike ladder rung bought ("ITM-3" / "ATM" / "OTM-1"), stamped by the engine at
   * entry. Null on rows before 2026-10-06 - see `lib/strikeRung.ts`. */
  strikeRung: string | null;
  /** The INDEX level the rule entered at, stamped at entry (`signal_entry`).
   *
   * ⚠️ NOT `entryPremium`, which is the option fill. The card shows both side
   * by side because a premium alone says nothing about where the index was
   * when the trade opened — operator, 2026-09-29: *"should have nifty price
   * along with option"*. */
  signalEntry: number | null;
  /** ISO timestamp when the trade was placed (entry order time). */
  entryAt: string;
  /** ISO timestamp when the trade closed; null while still open. */
  exitAt: string | null;
  /** LIVE (real broker) or PAPER (synthetic fill). Paper is badged + kept
   * out of the live numbers. */
  executionMode: ExecutionMode;
  contractSymbol: string | null;
  bias: "CALL" | "PUT" | null;
  lots: number | null;
  quantity: number | null;
  entryPremium: number | null;
  exitPremium: number | null;
  trailCount: number;
  exitReason: ExitReason;
  /** Gross P&L in INR (exit premium - entry premium) * qty. null while open
   *  OR when the backend hasn't computed it (MVP: always null until
   *  exit_premium is persisted). */
  pnl: number | null;
  /** Total trade cost: brokerage + STT + exchange + GST + stamp duty for
   * round trip. Stamped 0 at close; the operator enters the real
   * contract-note figure via the editable fees field (PATCH). null on
   * legacy/open rows — treated as 0 in the summary. */
  brokerage: number | null;
  /** Live trailing SL price at the last engine tick. Only meaningful for
   * OPEN trades — displayed on the Open Position card. Null for trades
   * the trail engine hasn't touched (fresh + legacy rows). */
  currentSlPrice: number | null;
  /** Peak premium seen since entry. Null for trades before the HWM
   * migration or those the engine hasn't polled yet. */
  highWaterMark: number | null;
  /** Where the swing-target engine books (bias fixed-stop mode). Null
   * for trail/ST-breach strategies — the ladder tiles cover those. */
  targetPrice: number | null;
  /** Trail ladder parameters stamped on the trade at entry ("move X pts
   * from entry → SL up Y pts"). Null on legacy rows; the card falls back
   * to instrument defaults for display. */
  trailTriggerPoints: number | null;
  trailAmountPoints: number | null;
  /** Rung-1 cost buffer stamped at entry (2026-07-15 ladder rework).
   * Null on pre-rework rows — the engine treats that as 0 (breakeven). */
  trailSlippagePoints: number | null;
  /** SL offset stamped at entry (P47) — sets the initial (rung-0) stop;
   * the reworked ladder rungs themselves no longer use it. */
  slOffsetPoints: number | null;
  /**
   * Set when a force exit was clicked while the exchange was shut: nothing
   * was sent to the broker and the position is STILL OPEN. The UI must say
   * "queued for the next open", never "exited" — an after-hours success
   * message is a lie about the account (operator, asked twice 2026-08-18).
   */
  queuedExitAt: string | null;
  /** INTRADAY (default, single contract) or POSITIONAL (basket). */
  horizon: Horizon;
  /** Broker product ("MIS"/"NRML"); null on legacy rows. */
  product: string | null;
  /** Nearest leg expiry of a basket (YYYY-MM-DD); null for intraday. */
  basketExpiry: string | null;
  /** Latest unrealized mark (V(t) − C) from the 15:25 MTM job. */
  lastMtm: number | null;
  /** Net entry cost C: debit > 0, credit < 0. */
  netEntryCost: number | null;
  /** Whole IST days the basket has been (or was) held; null for intraday. */
  holdingDays: number | null;
  /** Basket legs, ascending by legIndex; null for intraday rows. */
  legs: TradeLegEntry[] | null;
}

export type AlertType = "ENTRY" | "EXIT";

export type AlertDecision = "ACCEPTED" | "REJECTED";

export interface AlertLogEntry {
  id: string;
  strategyId: string;
  strategyName: string;
  /** ISO timestamp the alert was received. */
  receivedAt: string;
  type: AlertType;
  alertName: string;
  decision: AlertDecision;
  /** Reason text when REJECTED, else null. */
  reason: string | null;
  /** Raw webhook payload, JSON-stringified. null when the backend didn't
   * persist it (MVP — payload storage lands with a later change). */
  rawPayload: string | null;
}

export type DateRangeKey = "today" | "yesterday" | "week" | "month";

/** "all" is a sentinel for "match every strategy" — anything else is a real Strategy.id. */
export const ALL_STRATEGIES = "all";

export interface TradeLogFilters {
  range: DateRangeKey;
  strategyId: string;
  /** When "trades": only show trades. "alerts": only alerts. "both": show both panes. */
  show: "trades" | "alerts" | "both";
  /** Segregate paper from live. Omitted defaults to LIVE on the backend. */
  executionMode?: ExecutionMode;
}
